// Math types for scene layout: Vec2, Rect, Quad, Mat4.
// Mat4 is column-major, matching common GPU transform conventions.
// The transform math itself (TRS, inversion, quad/rect queries) lives in the
// Rust engine (`fluveria-scene` crate, `math.rs`); TS keeps only the types
// that cross the wasm boundary plus the few helpers the renderer needs.

export type Vec2 = [x: number, y: number];

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Four corners in order: top-left, top-right, bottom-right, bottom-left */
export interface Quad {
  tl: Vec2;
  tr: Vec2;
  br: Vec2;
  bl: Vec2;
}

/**
 * 4×4 column-major matrix stored as a flat Float64Array of 16 elements.
 * Index layout: m[col * 4 + row]
 *
 *   col0  col1  col2  col3
 *  [ 0]  [ 4]  [ 8]  [12]
 *  [ 1]  [ 5]  [ 9]  [13]
 *  [ 2]  [ 6]  [10]  [14]
 *  [ 3]  [ 7]  [11]  [15]
 */
export type Mat4 = Float64Array;

export function vec2(x: number, y: number): Vec2 {
  return [x, y];
}

export function vec2Clone(v: Vec2): Vec2 {
  return [v[0], v[1]];
}

/** Create a 2D translation matrix (translates in x, y). */
export function mat4Translate(tx: number, ty: number): Mat4 {
  const m = new Float64Array(16);
  m[0] = 1; m[5] = 1; m[10] = 1; m[15] = 1;
  m[12] = tx;
  m[13] = ty;
  return m;
}

/**
 * The layer's framebuffer pixels per shape unit along each axis: the lengths
 * of the transform's two basis vectors.
 *
 * The engine asks for these by name (`materialDrawBounds`), because a draw's
 * expansion is a distance in pixels and the bounds it expands are in shape
 * units. Kept here rather than in one renderer because both the WebGPU and the
 * canvas path need the same two numbers from the same matrix.
 */
export function worldScaleOf(fromLocal: Mat4): Vec2 {
  return [
    Math.sqrt(fromLocal[0] * fromLocal[0] + fromLocal[1] * fromLocal[1]),
    Math.sqrt(fromLocal[4] * fromLocal[4] + fromLocal[5] * fromLocal[5]),
  ];
}

/** Transform a 2D point by a Mat4 (assumes z=0, w=1). */
export function mat4TransformPoint(m: Mat4, p: Vec2): Vec2 {
  return [
    m[0] * p[0] + m[4] * p[1] + m[12],
    m[1] * p[0] + m[5] * p[1] + m[13],
  ];
}
